import { useCallback, useEffect, useRef, useState } from "react";
import { FOMO_BASE } from "@/config";
import { api } from "@/lib/api";
import { setFomoValue } from "@/lib/fomoStore";
import { cn } from "@/lib/utils";

const SPARKS = [0, 1, 2, 3, 4, 5, 6, 7];
const POLL_MS = 30000;

// Backend-driven — full cross-device parity. The number comes from GET /api/config/public
// (`waitlist_total`), the SAME source the mobile app reads, so web-desktop, web-mobile and
// native all show the IDENTICAL count. Polls every 30s so it stays live with no rebuild.
async function fetchCount() {
  try {
    const cfg = await api.getPublicConfig();
    if (cfg && typeof cfg.waitlist_total === "number") return cfg.waitlist_total;
  } catch {
    /* offline / unreachable → keep last value or fall back to FOMO_BASE */
  }
  return null;
}

export function WaitlistCounter({ className = "" }) {
  const [display, setDisplay] = useState(null);
  const [live, setLive] = useState(false);
  const [offline, setOffline] = useState(false);
  const [bursts, setBursts] = useState([]);
  const rafRef = useRef(null);
  const displayRef = useRef(0);
  const revealedRef = useRef(false);

  const tweenTo = useCallback((from, to, duration) => {
    cancelAnimationFrame(rafRef.current);
    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const val = Math.round(from + (to - from) * ease(p));
      displayRef.current = val;
      setFomoValue(val);
      setDisplay(val);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }, []);

  const apply = useCallback(
    (next) => {
      if (typeof next !== "number") return;
      setLive(true);
      if (!revealedRef.current) {
        revealedRef.current = true;
        tweenTo(0, next, 1400); // count-up reveal on first load
      } else if (next > displayRef.current) {
        const delta = next - displayRef.current;
        tweenTo(displayRef.current, next, 700);
        const id = `${Date.now()}-${Math.random()}`;
        setBursts((b) => [...b, { id, delta }]);
        setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1500);
      } else if (next !== displayRef.current) {
        displayRef.current = next;
        setDisplay(next);
        setFomoValue(next);
      }
    },
    [tweenTo],
  );

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const n = await fetchCount();
      if (!alive) return;
      if (typeof n === "number") apply(n);
      else if (!revealedRef.current) setOffline(true);
    };
    load();
    const iv = setInterval(() => {
      if (!document.hidden) load();
    }, POLL_MS);
    return () => {
      alive = false;
      clearInterval(iv);
      cancelAnimationFrame(rafRef.current);
    };
  }, [apply]);

  const hasValue = live || offline;
  const shown = live ? display ?? 0 : FOMO_BASE;

  return (
    <div className={cn("flex flex-col items-center", className)} data-testid="waitlist-count">
      {live && (
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-bloq-mint/30 bg-bloq-mint/10 px-3 py-1">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-bloq-mint opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-bloq-mint" />
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-bloq-mint">Live</span>
        </div>
      )}

      <div className="relative flex items-center justify-center">
        <div className="pointer-events-none absolute h-28 w-56 rounded-full bg-bloq-mint/10 blur-2xl" />
        {live &&
          bursts.map((b) => (
            <div key={b.id} className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="relative h-0 w-0">
                <span className="counter-glow" />
                <span className="counter-ring" />
                <span className="counter-ring" style={{ animationDelay: "130ms" }} />
                {SPARKS.map((i) => (
                  <span key={i} className="counter-spark" style={{ "--a": `${i * 45}deg` }} />
                ))}
                <span className="counter-plus">+{b.delta}</span>
              </div>
            </div>
          ))}
        <div
          className={cn(
            "counter-number relative font-heading text-6xl font-bold leading-none tabular-nums bloq-gradient-text sm:text-7xl transition-opacity",
            !live && "opacity-40",
            live && bursts.length && "counter-pop",
          )}
          data-testid="waitlist-counter-value"
        >
          {hasValue ? shown.toLocaleString() : <span className="inline-block animate-pulse tracking-widest">···</span>}
        </div>
      </div>

      <div className="mt-2.5 text-sm font-medium text-white/70">
        on the waitlist <span className="text-bloq-muted">· securing their handle</span>
      </div>
    </div>
  );
}
